import { http } from '@utilities/http';
import {
  DefaultUserProfile,
  type Charity,
  type Cause,
  type Initiative,
  type Claim,
  type CategoryGrouping,
  type Category,
  type CategorySearchResult,
  type UserProfile,
  type Donation,
  type Payment,
  type Match,
  type SearchResult,
  type AreaFoodBank,
  type AreaMediaStation,
  type AreaShelter,
  type DonationDto,
  type Distribution,
  type WYRQuestion,
  type DashboardDataResponse,
} from '@types';
import * as consts from '@utilities/consts';

export const createClaim = async (
  charityId: string,
  determinationLetter: File | null,
): Promise<boolean> => {
  try {
    if (determinationLetter) {
      let pdfUrl = await uploadAsset(
        charityId,
        'application/pdf',
        determinationLetter,
        `/claim-documents/[charityId]-[userId].pdf`,
      );
    }
    let response = await http.post(`/Representatives`, JSON.stringify(charityId), {
      requiresAuth: true,
    });
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

export const verifyClaim = async (charityId: string, secret: string): Promise<boolean> => {
  try {
    let response = await http.put(
      `/Representatives`,
      JSON.stringify({ charityId: charityId, secret: secret }),
      { requiresAuth: true },
    );
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

export const getClaims = async (): Promise<Claim[]> => {
  try {
    let response = await http.get(`/Representatives`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getCharityAsRepresentative = async (charityId: string): Promise<Charity | null> => {
  try {
    let response = await http.get(`/Charities/${charityId}`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getCharity = async (charityId: string): Promise<Charity | null> => {
  try {
    let response = await http.get(`/Charities/${charityId}`, {});
    return response.data;
  } catch (error) {
    return null;
  }
};

export const saveCharity = async (charity: Charity): Promise<Charity | null> => {
  if (charity.logoImageFile) {
    charity.logoImageUrl = await uploadAsset(
      charity.id,
      'image/webp',
      charity.logoImageFile,
      `/charity-images/${charity.id}_logoimage.webp`,
    );
  }
  let response = await http.put(
    `/Charities/`,
    {
      ...charity,
      verticalImageFile: null,
      horizontalImageFile: null,
    },
    { requiresAuth: true },
  );
  return response.data;
};

export const publishCharity = async (
  charityId: string,
  publish: boolean,
): Promise<Charity | null> => {
  let response = await http.put(`/Charities/${charityId}/publish`, publish, { requiresAuth: true });
  return response.data;
};

export const uploadAsset = async (
  charityId: string,
  contentType: string,
  file: File,
  targetPath: string,
): Promise<string> => {
  const formData = new FormData();
  formData.append('charityId', charityId);
  formData.append('contentType', contentType);
  formData.append('file', file);
  formData.append('url', `${targetPath}`);
  let response = await http.post(`/assets`, formData, {
    requiresAuth: true,
    headers: {
      Accept: 'application/json',
    },
  });
  return response.data;
};

export const getInitiative = async (initiativeId: string): Promise<Initiative | null> => {
  try {
    let response = await http.get(`/Initiatives/${initiativeId}`, {});
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getInitiativeMatches = async (
  charityId: string,
  initiativeId: string,
): Promise<Match[]> => {
  try {
    let response = await http.get(`/Initiatives/${charityId}/${initiativeId}/Matches`, {});
    return [...response.data].sort(
      (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    );
  } catch (error) {
    return [];
  }
};

export const saveInitiative = async (initiative: Initiative): Promise<Initiative | null> => {
  if (initiative.verticalImageFile) {
    initiative.verticalImageUrl = await uploadAsset(
      initiative.charityId,
      'image/webp',
      initiative.verticalImageFile,
      `/initiative-images/${initiative.id}_verticalimage.webp`,
    );
  }
  if (initiative.horizontalImageFile) {
    initiative.horizontalImageUrl = await uploadAsset(
      initiative.charityId,
      'image/webp',
      initiative.horizontalImageFile,
      `/initiative-images/${initiative.id}_horizontalimage.webp`,
    );
  }
  let response = await http.put(
    `/initiatives/`,
    {
      ...initiative,
      verticalImageFile: null,
      horizontalImageFile: null,
    },
    { requiresAuth: true },
  );
  return response.data;
};

export const publishInitiative = async (
  charityId: string,
  initiativeId: string,
  publish: boolean,
): Promise<Charity | null> => {
  let response = await http.put(
    `/Charities/${charityId}/Initiatives/${initiativeId}/publish`,
    publish,
    { requiresAuth: true },
  );
  return response.data;
};

export const createInitiative = async (initiative: Initiative): Promise<string> => {
  try {
    let response = await http.post(
      `/Initiatives`,
      JSON.stringify({
        ...initiative,
        verticalImageFile: null,
        horizontalImageFile: null,
      }),
      {
        requiresAuth: true,
      },
    );
    if (response.status == 201) {
      initiative.id = response.data.id;
      await saveInitiative(initiative);
    }
    return response.status === 201 ? response.data.id : '';
  } catch (error) {
    return '';
  }
};

export const getInitiatives = async (charityId: string): Promise<Initiative[]> => {
  try {
    let response = await http.get(`/Charities/${charityId}/Initiatives`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getCharityDonations = async (
  charityId: string,
  year: number,
  month: number,
): Promise<DonationDto[]> => {
  try {
    let response = await http.get(`/Charities/${charityId}/Donations/${year}/${month}`, {
      requiresAuth: true,
    });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getInitiativesAsRepresentative = async (charityId: string): Promise<Initiative[]> => {
  try {
    let response = await http.get(`/Charities/${charityId}/Initiatives`, {
      requiresAuth: true,
    });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getInitiativeAsRepresentative = async (
  charityId: string,
  initiativeId: string,
): Promise<Initiative | null> => {
  try {
    let response = await http.get(`/Charities/${charityId}/Initiatives/${initiativeId}`, {
      requiresAuth: true,
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getCategoryGroupings = async (): Promise<CategoryGrouping[]> => {
  try {
    let response = await http.get(`/CategoryGroupings`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getCategories = async (): Promise<Category[]> => {
  try {
    let response = await http.get(`/Categories`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getCharityCategories = async (charityId: string): Promise<Category[]> => {
  try {
    let response = await http.get(`/Charities/${charityId}/Categories`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getCauses = async (): Promise<Cause[]> => {
  try {
    let response = await http.get(`/Causes`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getCause = async (causeId: string): Promise<Cause> => {
  let response = await http.get(`/causes/${causeId}`, {});
  return response.data;
};

export const getCategorySearchResults = async (
  categoryId: string,
): Promise<CategorySearchResult[]> => {
  let response = await http.get(`/categories/search/${categoryId}`, {});
  return response.data;
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<boolean> => {
  try {
    let response = await http.put(
      `/UserProfiles/Password`,
      JSON.stringify({
        currentPassword: currentPassword,
        newPassword: newPassword,
      }),
      { requiresAuth: true },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const changeEmail = async (newEmail: string): Promise<boolean> => {
  try {
    let response = await http.put(`/UserProfiles/Email`, JSON.stringify({ newEmail }), {
      requiresAuth: true,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const saveUserProfile = async (userProfile: UserProfile): Promise<boolean> => {
  try {
    let response = await http.put(`/UserProfiles`, JSON.stringify(userProfile), {
      requiresAuth: true,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    let response = await http.get(`/UserProfiles`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return DefaultUserProfile;
  }
};

export const createPayment = async (payment: Payment): Promise<Payment | null> => {
  try {
    let response = await http.post(
      `/Payments`,
      JSON.stringify({
        userId: payment.userId,
        amountMinor: payment.amountMinor,
        donations: payment.donations,
      }),
      {
        requiresAuth: true,
      },
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const approvePayment = async (payment: Payment): Promise<Payment | null> => {
  try {
    let response = await http.put(
      `/Payments`,
      JSON.stringify({
        id: payment.id,
        orderId: payment.orderId,
        userId: payment.userId,
        amountMinor: payment.amountMinor,
      }),
      { requiresAuth: true },
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getUserDonations = async (): Promise<Donation[]> => {
  try {
    let response = await http.get(`/Donations`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getPayment = async (paymentId: string): Promise<Payment | null> => {
  try {
    let response = await http.get(`/Payments/${paymentId}`, { requiresAuth: true });
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getSearchResults = async (query: string): Promise<SearchResult[]> => {
  let response = await http.get(
    `/search/?searchType=charity-and-initiative&query=${query}&skip=0&size=1000`,
    {},
  );
  return response.data;
};

export const getAreaFoodBank = async (state: string, id: string): Promise<AreaFoodBank> => {
  try {
    let response = await http.get(`/Areas/foodbanks/${state}/${id}`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAreaFoodBanks = async (state: string): Promise<AreaFoodBank[]> => {
  try {
    let response = await http.get(`/Areas/foodbanks/${state}`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAreaMediaStation = async (state: string, id: string): Promise<AreaMediaStation> => {
  try {
    let response = await http.get(`/Areas/mediastations/${state}/${id}`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAreaMediaStations = async (state: string): Promise<AreaMediaStation[]> => {
  try {
    let response = await http.get(`/Areas/mediastations/${state}`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAreaShelter = async (state: string, id: string): Promise<AreaShelter> => {
  try {
    let response = await http.get(`/Areas/shelters/${state}/${id}`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAreaShelters = async (state: string): Promise<AreaShelter[]> => {
  try {
    let response = await http.get(`/Areas/shelters/${state}`, {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAllDonations = async (startDate: Date, endDate: Date): Promise<Donation[]> => {
  try {
    let response = await http.post(
      `/Donations`,
      JSON.stringify({
        startDate: startDate,
        endDate: endDate,
      }),
      { requiresAuth: true },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const updateDonationStatus = async (
  donation: Donation,
  status: string,
  notes: string,
  distributionID: string,
): Promise<Payment | null> => {
  try {
    console.log(
      JSON.stringify({
        id: donation.id,
        paymentId: donation.paymentId,
        status: status,
        notes: notes,
        distributionID: distributionID,
      }),
    );
    let response = await http.put(
      `/Donations`,
      JSON.stringify({
        id: donation.id,
        paymentId: donation.paymentId,
        status: status,
        notes: notes,
        distributionID: distributionID,
      }),
      {
        requiresAuth: true,
      },
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const createDistribution = async (
  distribution: Distribution,
): Promise<Distribution | null> => {
  let response = await http.post(
    `/Distributions/`,
    JSON.stringify({
      ...distribution,
    }),
    { requiresAuth: true },
  );
  return response.data;
};

export const saveCredit = async (
  donationId: string,
  includeAllAppliedMatches: boolean,
  notes: number,
): Promise<Distribution | null> => {
  console.log(
    JSON.stringify({
      donationId: donationId,
      includeAllAppliedMatches: includeAllAppliedMatches,
      notes: notes,
    }),
  );
  let response = await http.post(
    `/Distributions/Credit`,
    JSON.stringify({
      donationId: donationId,
      includeAllAppliedMatches: includeAllAppliedMatches,
      notes: notes,
    }),
    { requiresAuth: true },
  );
  return response.data;
};

export const getWYRQuestion = async (id: string): Promise<WYRQuestion | null> => {
  try {
    let response = await http.get(`/WouldYouRather/${id}`, {});
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getInitiativesByTags = async (tags: string[]): Promise<Initiative[]> => {
  try {
    let response = await http.post(`/WouldYouRather`, JSON.stringify(tags), {});
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getDashboardData = async (): Promise<DashboardDataResponse | null> => {
  try {
    let response = await http.get(`/Reports/Dashboard`, {});
    return response.data;
  } catch (error) {
    return null;
  }
};
